import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { trackFBCustomEvent } from "@/lib/fbPixel";
import heroPoster from "@/assets/hero-poster.webp";
import heroPosterMobile from "@/assets/hero-poster-mobile.webp";
import {
  GraduationCap,
  FileText,
  Globe,
  Users,
  Award,
  CheckCircle2,
  TrendingUp,
  Target,
  Shield,
  ArrowRight,
  Loader2,
  Sparkles,
  Phone,
} from "lucide-react";
import Footer from "@/components/Footer";
import ScrollHeader from "@/components/ScrollHeader";

import CTAbg from "@/assets/hero-slide-3.webp";
import img02 from "@/assets/Img_02.webp";

import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useState, useEffect, lazy, Suspense, useCallback } from "react";
// Firebase/Firestore and Supabase are loaded on demand (see loadNewsArticles) so the
// homepage's critical path doesn't ship ~270 KiB of SDK before first paint.

import { toast } from "sonner";
import { format } from "date-fns";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useCountingAnimation } from "@/hooks/useCountingAnimation";
import { useTypingEffect } from "@/hooks/useTypingEffect";
import { useParallax } from "@/hooks/useParallax";
import { SEOHead } from "@/components/SEOHead";
import { OrganizationSchema } from "@/components/OrganizationSchema";
import { trackFBEvent } from "@/lib/fbPixel";
import FloatingButtons from "@/components/FloatingButtons";
import HomeRegisterButton from "@/components/HomeRegisterButton";
import ErrorBoundary from "@/components/ErrorBoundary";

// Lazy load below-the-fold components
const GoogleReviews = lazy(() => import("@/components/GoogleReviews"));
const HomeFeaturedCourses = lazy(() => import("@/components/HomeFeaturedCourses"));

// University images - loaded on demand via native lazy loading
import uni1 from "@/assets/canterbury.png";
import uni2 from "@/assets/UWE.png";
import uni3 from "@/assets/demontfort.png";
import uni4 from "@/assets/kaplan.png";
import uni5 from "@/assets/northumbria.png";
import uni6 from "@/assets/nottingham.png";
import uni7 from "@/assets/roehampton.png";
import uni8 from "@/assets/solent.png";

const partnerUniversities = [
  { img: uni1, name: "Canterbury Christ Church University" },
  { img: uni2, name: "University of the West of England (UWE Bristol)" },
  { img: uni3, name: "De Montfort University" },
  { img: uni4, name: "Kaplan International Pathways" },
  { img: uni5, name: "Northumbria University Newcastle" },
  { img: uni6, name: "University of Nottingham" },
  { img: uni7, name: "University of Roehampton London" },
  { img: uni8, name: "Solent University Southampton" },
];
interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image_url: string;
  published_at: string;
}
const Index = () => {
  const [courseSearch, setCourseSearch] = useState("");
  const [studyLevel, setStudyLevel] = useState("Undergraduate");
  const [recommendation, setRecommendation] = useState("");
  const [loading, setLoading] = useState(false);
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [showHeroVideo, setShowHeroVideo] = useState(false);
  const [heroVideoSrc, setHeroVideoSrc] = useState<string | null>(null);
  const [heroVideoReady, setHeroVideoReady] = useState(false);

  const statsSection = useScrollAnimation(0.1);
  const expertiseSection = useScrollAnimation(0.1);
  const whyChooseSection = useScrollAnimation(0.3);
  const servicesSection = useScrollAnimation(0.1);
  const newsSection = useScrollAnimation(0.3);
  const partnersSection = useScrollAnimation(0.2);
  const ctaSection = useScrollAnimation(0.3);
  const ctaParallax = useParallax(-0.2);

  // Counting animations for hero stats
  const yearsCount = useCountingAnimation({
    end: 17,
    duration: 2000,
    isVisible: statsSection.isVisible,
    suffix: "+",
  });
  const studentsCount = useCountingAnimation({
    end: 1800,
    duration: 2500,
    isVisible: statsSection.isVisible,
    suffix: "+",
  });
  const universitiesCount = useCountingAnimation({
    end: 550,
    duration: 2000,
    isVisible: statsSection.isVisible,
    suffix: "+",
  });

  const typedText = useTypingEffect({
    text: "Get in Touch with Us Today",
    speed: 80,
    isVisible: ctaSection.isVisible,
  });
  useEffect(() => {
    loadNewsArticles();
  }, []);

  // Hero video: loaded after first paint on all screens. The poster stays the LCP
  // element; the video fades in later, so it never enters the LCP/CLS window.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const conn = (navigator as any).connection;
    if (conn?.saveData) return;
    if (/(^|\W)(slow-2g|2g|3g)$/.test(conn?.effectiveType ?? "")) return;

    const isMobileViewport = window.matchMedia("(max-width: 767px)").matches;

    let done = false;
    const start = () => {
      if (done) return;
      done = true;
      const load = isMobileViewport
        ? import("@/assets/hero-mobile.mp4")
        : import("@/assets/hero-desktop.mp4");
      load.then((mod) => {
        setHeroVideoSrc(mod.default);
        setShowHeroVideo(true);
      });
    };
    const ric = (window as any).requestIdleCallback?.(start, { timeout: 2500 });
    const timer = window.setTimeout(start, 1500);
    return () => {
      window.clearTimeout(timer);
      (window as any).cancelIdleCallback?.(ric);
    };
  }, []);


  // Load reCAPTCHA v3 only once the visitor engages with a form control — loading it
  // on idle/scroll still cost ~80ms of forced reflow during page load.
  useEffect(() => {
    let done = false;
    const load = (event?: Event) => {
      if (done) return;
      // only react to genuine user input on a form control
      if (event) {
        const t = event.target as HTMLElement | null;
        if (!event.isTrusted) return;
        if (!t || !/^(INPUT|TEXTAREA|SELECT|BUTTON)$/.test(t.tagName)) return;
      }
      done = true;
      if (!document.querySelector(`script[src*="recaptcha"]`)) {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=6LetCiUsAAAAAAO8B8fBGptQSMEcKK-hku7wqMA7`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      cleanup();
    };
    const events = ["pointerdown", "keydown"] as const;
    events.forEach((e) => document.addEventListener(e, load, { passive: true }));
    function cleanup() {
      events.forEach((e) => document.removeEventListener(e, load));
    }
    return cleanup;
  }, []);



  const loadNewsArticles = async () => {
    try {
      const [{ db }, { collection, query, where, orderBy, limit, getDocs }] = await Promise.all([
        import("@/integrations/firebase/client"),
        import("firebase/firestore"),
      ]);
      const q = query(
        collection(db, "news_articles"),
        where("is_published", "==", true),
        orderBy("published_at", "desc"),
        limit(3)
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNewsArticles(data as unknown as NewsArticle[]);
    } catch (error) {
      console.error("Error loading news articles:", error);
      toast.error("Failed to load news articles");
    } finally {
      setNewsLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        pagePath="/"
        defaultTitle="ULC World – UK Student Visa & University Experts"
        defaultDescription="Get expert help with UK study visas, university applications, dependent visas, and immigration. 17+ years of trusted guidance from ULC World."
        defaultKeywords="UK student visa, Study in the UK, UK university application, UK education consultant, UK admissions support, Student visa consultant, UK immigration consultant, UK visa processing Sri Lanka, UK study abroad agency, UK university admissions"
      />
      <OrganizationSchema />
      <ScrollHeader currentPage="home" />

      {/* Hero Section - AI Course Finder */}

      <section className="relative overflow-hidden min-h-[680px] md:min-h-[900px] flex items-center pt-24 pb-12">
        {/* Background media — poster is the LCP element, video fades in after first paint on all screens */}
        <div className="absolute inset-0 w-full h-full">
          <picture className="absolute inset-0 w-full h-full">
            <source media="(max-width: 767px)" srcSet={heroPosterMobile} type="image/webp" />
            <img
              src={heroPoster}
              alt="Students studying in the UK"
              width={1920}
              height={1080}
              {...{ fetchpriority: "high" }}
              decoding="async"
              className="w-full h-full object-cover"
            />
          </picture>
          {showHeroVideo && heroVideoSrc && !videoError && (
            <video
              src={heroVideoSrc}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                heroVideoReady ? "opacity-100" : "opacity-0"
              }`}
              playsInline
              autoPlay
              loop
              muted
              preload="auto"
              poster={heroPoster}
              aria-hidden="true"
              onCanPlay={() => setHeroVideoReady(true)}
              onError={() => setVideoError(true)}
            />
          )}
          <div className="absolute inset-0 bg-[hsl(190_40%_16%/0.55)]" />
        </div>


        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center text-white">
            <h1 className="text-[2.75rem] md:text-7xl lg:text-8xl font-extrabold uppercase tracking-tight leading-none mb-4 drop-shadow-[0_2px_18px_rgba(0,0,0,0.35)]">
              STUDY IN UK
            </h1>
            <p className="text-[1.05rem] md:text-2xl font-semibold mb-10 text-white drop-shadow-[0_1px_10px_rgba(0,0,0,0.3)]">
              Your trusted global education and immigration advisor since 2008
            </p>


            {/* Course Finder Card */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">

              <div className="inline-flex items-center gap-2 px-5 py-2 bg-primary backdrop-blur-sm rounded-full border border-primary/30 mb-6">
                <GraduationCap className="w-5 h-5 text-primary-foreground" />
                <span className="text-primary-foreground font-bold text-sm uppercase tracking-wider">
                  AI-Powered Course Advisor
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="text-left">
                  <label htmlFor="qualifications" className="block text-secondary font-semibold mb-3 text-sm">
                    Your Qualifications & Interests
                  </label>
                  <input
                    id="qualifications"
                    type="text"
                    placeholder="E.g., Bachelor's in Computer Science, interested in AI"
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors text-foreground"
                  />
                </div>
                <div className="text-left">
                  <label htmlFor="study-level" className="block text-secondary font-semibold mb-3 text-sm">
                    Select Study Level
                  </label>
                  <select
                    id="study-level"
                    value={studyLevel}
                    onChange={(e) => setStudyLevel(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors text-foreground appearance-none bg-white"
                  >
                    <option>Undergraduate</option>
                    <option>Postgraduate</option>
                    <option>PhD/Doctorate</option>
                    <option>Foundation</option>
                    <option>Pre-Masters</option>
                  </select>
                </div>
              </div>
              <Button
                size="lg"
                onClick={async () => {
                  if (!courseSearch) {
                    toast.error("Please enter your qualifications and interests");
                    return;
                  }
                  setLoading(true);
                  setRecommendation("");
                  try {
                    // reCAPTCHA loads on first form interaction; wait briefly if it is still in flight
                    let g = (window as any).grecaptcha;
                    if (!g) {
                      if (!document.querySelector(`script[src*="recaptcha"]`)) {
                        const s = document.createElement("script");
                        s.src = `https://www.google.com/recaptcha/api.js?render=6LetCiUsAAAAAAO8B8fBGptQSMEcKK-hku7wqMA7`;
                        s.async = true;
                        document.head.appendChild(s);
                      }
                      for (let i = 0; i < 80 && !g; i++) {
                        await new Promise((r) => setTimeout(r, 100));
                        g = (window as any).grecaptcha;
                      }
                    }
                    if (!g) {
                      toast.error("Verification not ready, please try again.");
                      setLoading(false);
                      return;
                    }
                    const recaptchaToken: string = await new Promise((resolve, reject) =>
                      g.ready(async () => {
                        try {
                          resolve(await g.execute("6LetCiUsAAAAAAO8B8fBGptQSMEcKK-hku7wqMA7", { action: "course_advisor" }));
                        } catch (e) {
                          reject(e);
                        }
                      })
                    );
                    const response = await fetch("https://us-central1-ulcworld.cloudfunctions.net/aiCourseAdvisor", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        qualifications: courseSearch.slice(0, 500),
                        interests: `${studyLevel} level studies`,
                        recaptchaToken,
                      }),
                    });
                    const data = await response.json();
                    if (!response.ok) throw new Error(data.error || "Failed to fetch recommendation");
                    if (data.error) {
                      toast.error(data.error);
                      return;
                    }
                    setRecommendation(data.recommendation);
                    
                    // Track successful AI course search with Facebook Pixel
                    trackFBEvent('Search', {
                      content_category: 'Course Finder',
                      search_string: studyLevel
                    });
                    
                    toast.success("Recommendation generated!");
                  } catch (error: any) {
                    console.error("Error getting recommendation:", error);
                    toast.error("Failed to get recommendation. Please try again.");
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-lg py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Recommendation...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Find My Course
                  </>
                )}
              </Button>

              {recommendation && (
                <div className="mt-6 p-6 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl border border-primary/20 text-left">
                  <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-foreground">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Your Personalized Recommendation
                  </h3>
                  <div className="prose prose-sm max-w-none text-foreground whitespace-pre-line">{recommendation}</div>
                </div>
              )}
            </div>

            {/* Stats */}
            <div
              ref={statsSection.ref}
              className={`grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-16 max-w-3xl mx-auto transition-all duration-1000 ${statsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
            >
              <div
                className="text-center animate-fade-in"
                style={{
                  animationDelay: "0.4s",
                }}
              >
                <div className="text-3xl md:text-5xl font-bold mb-2 text-white">{yearsCount}</div>
                <div className="text-xs md:text-base text-white/90">Years Experience</div>
              </div>
              <div
                className="text-center animate-fade-in"
                style={{
                  animationDelay: "0.5s",
                }}
              >
                <div className="text-3xl md:text-5xl font-bold mb-2 text-white">{studentsCount}</div>
                <div className="text-xs md:text-base text-white/90">Student Placements</div>
              </div>
              <div
                className="text-center animate-fade-in col-span-2 sm:col-span-1"
                style={{
                  animationDelay: "0.6s",
                }}
              >
                <div className="text-3xl md:text-5xl font-bold mb-2 text-white">{universitiesCount}</div>
                <div className="text-xs md:text-base text-white/90">Scholarships</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section
        ref={expertiseSection.ref}
        className={`py-24 bg-gradient-to-br from-background via-primary/5 to-accent/10 relative overflow-hidden transition-all duration-2000 ${expertiseSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-20 left-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"
            style={{
              animationDelay: "2s",
            }}
          ></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 animate-fade-in">
            <span className="text-primary font-bold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 mt-2">Comprehensive Immigration & Education Services</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Your trusted global education and immigration advisor since 2008
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: GraduationCap,
                title: "Education Consultancy",
                desc: "Expert guidance for UK university admissions and course selection",
                color: "from-blue-500 to-cyan-500",
              },
              {
                icon: FileText,
                title: "Application Processing",
                desc: "End-to-end support for university and visa applications",
                color: "from-purple-500 to-pink-500",
              },
              {
                icon: Globe,
                title: "Visa Processing",
                desc: "IAA Certified immigration advice and visa services",
                color: "from-orange-500 to-red-500",
              },
              {
                icon: Users,
                title: "Migration Support",
                desc: "Family reunion, spouse visa, and settlement assistance",
                color: "from-green-500 to-emerald-500",
              },
            ].map((service, i) => (
              <div
                key={i}
                className={`group relative p-8 bg-card rounded-3xl border border-border hover:border-primary/50 transition-all duration-700 overflow-hidden ${expertiseSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{
                  animationDelay: `${i * 0.15}s`,
                  boxShadow: "var(--shadow-card)",
                  transitionDelay: `${i * 500}ms`,
                }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative z-10">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <service.icon className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose ULC World */}
      <section
        ref={whyChooseSection.ref}
        className={`py-24 bg-gradient-to-br from-muted/30 via-background to-muted/20 transition-all duration-1000 ${whyChooseSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <span className="text-primary font-bold text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 mt-2">Why We're the Right Partner for You</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Excellence in UK education and immigration services with proven track record
            </p>
          </div>

          {/* Key Differentiators */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: TrendingUp,
                title: "17+ Years Experience",
                desc: "Over a decade of expertise in UK education and immigration",
                gradient: "from-primary/20 to-primary/5",
              },
              {
                icon: Target,
                title: "Student Satisfaction",
                desc: "Rated highly for transparency, support, and outcomes",
                gradient: "from-accent/20 to-accent/5",
              },
              {
                icon: Shield,
                title: "IAA Registered",
                desc: "We are proud to be registered with the Immigration Advisers Authority (IAA)",
                gradient: "from-green-500/20 to-green-500/5",
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`relative p-8 bg-gradient-to-br ${item.gradient} rounded-3xl border border-border hover:border-primary/50 transition-all duration-700 animate-scale-in group ${whyChooseSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{
                  animationDelay: `${i * 0.1}s`,
                  boxShadow: "var(--shadow-card)",
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <item.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Personalized Guidance",
                desc: "Tailored advice for your unique journey",
              },
              {
                title: "Strong University Networks",
                desc: "Partnerships with 50+ UK universities",
              },
              {
                title: "Experienced Counselors",
                desc: "UK-trained education consultants",
              },
              {
                title: "One-Stop Solution",
                desc: "From application to arrival support",
              },
              {
                title: "End-to-End Support",
                desc: "Guidance throughout your entire journey",
              },
              {
                title: "Ethical Practices",
                desc: "Transparent pricing and honest advice",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className={`flex items-start gap-4 p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-md transition-all duration-700 animate-fade-in ${whyChooseSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{
                  animationDelay: `${i * 0.1}s`,
                  transitionDelay: `${i * 10}ms`,
                }}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Student & Immigration Services */}
      <section
        ref={servicesSection.ref}
        className={`py-24 bg-gradient-to-br from-accent/5 via-background to-primary/10 relative overflow-hidden transition-all duration-1000 ${servicesSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-40 left-40 w-[500px] h-[500px] bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-40 right-40 w-[500px] h-[500px] bg-gradient-to-br from-accent/20 to-transparent rounded-full blur-3xl animate-pulse"
            style={{
              animationDelay: "2s",
            }}
          ></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-up">
              <span className="text-primary font-bold text-sm uppercase tracking-wider">Complete Support</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 mt-2">Student & Immigration Services</h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                We offer world-class education consulting aligned with UK government standards to help your career soar
                to success. From initial counseling to post-arrival support.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  {
                    icon: GraduationCap,
                    title: "IELTS/PTE Preparation",
                    desc: "Expert coaching programs",
                  },
                  {
                    icon: Globe,
                    title: "Student Visa Support",
                    desc: "Tier 4 visa guidance",
                  },
                  {
                    icon: FileText,
                    title: "University Applications",
                    desc: "50+ partner institutions",
                  },
                  {
                    icon: Users,
                    title: "Family Visa Services",
                    desc: "Spouse & dependent visas",
                  },
                  {
                    icon: Phone,
                    title: "Pre-Departure Briefing",
                    desc: "UK arrival preparation",
                  },
                  {
                    icon: Award,
                    title: "Post-Arrival Support",
                    desc: "Settling in assistance",
                  },
                ].map((service, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all duration-500 ${servicesSection.isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
                    style={{
                      transitionDelay: `${i * 10}ms`,
                    }}
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{service.title}</h3>
                      <p className="text-sm text-muted-foreground">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/about">
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-md"
                  >
                    Learn More About Our Services
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/10 to-transparent rounded-3xl blur-3xl"></div>
              <div
                className="relative w-full h-[500px] rounded-3xl flex items-center justify-center overflow-hidden border border-primary/20 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${img02})`,
                  boxShadow: "var(--shadow-elegant)",
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/40"></div>
                <Award className="w-40 h-40 text-primary/30 absolute top-10 right-10 animate-float" />
                <Globe
                  className="w-32 h-32 text-accent/30 absolute bottom-10 left-10 animate-float"
                  style={{
                    animationDelay: "1s",
                  }}
                />
                <div className="relative z-10 text-center text-white p-8">
                  <GraduationCap className="w-32 h-32 text-primary mx-auto mb-6" />
                  <h3 className="text-3xl font-bold mb-4">Your Journey Starts Here</h3>
                  <p className="text-secondary-foreground/80">Trusted education & immigration partner</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <ErrorBoundary name="home-featured-courses" silent>
        <Suspense fallback={<div className="min-h-[700px] py-24" aria-hidden="true" />}>
          <HomeFeaturedCourses />
        </Suspense>
      </ErrorBoundary>

      {/* Latest News Section */}
      <section
        ref={newsSection.ref}
        className={`py-24 bg-gradient-to-br from-primary/5 via-background to-accent/5 relative overflow-hidden transition-all duration-1000 ${newsSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        <div className="absolute inset-0 opacity-40">
          <div
            className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse"
            style={{
              animationDelay: "1s",
            }}
          ></div>
          <div
            className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"
            style={{
              animationDelay: "3s",
            }}
          ></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 animate-fade-in">
            <span className="text-primary font-bold text-sm uppercase tracking-wider">Stay Updated</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 mt-2">Latest News & Updates</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Stay informed with the latest news in UK education and immigration
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {newsLoading ? (
              // Loading skeleton with shimmer effect
              [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-card rounded-2xl border border-border overflow-hidden relative animate-fade-in"
                  style={{
                    animationDelay: `${i * 0.1}s`,
                  }}
                >
                  <div className="h-48 bg-gradient-to-br from-muted via-muted/50 to-muted animate-pulse relative overflow-hidden">
                    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                  </div>
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-gradient-to-r from-muted to-muted/50 rounded w-1/3 animate-pulse"></div>
                    <div className="h-6 bg-gradient-to-r from-muted to-muted/50 rounded animate-pulse"></div>
                    <div
                      className="h-4 bg-gradient-to-r from-muted to-muted/50 rounded animate-pulse"
                      style={{
                        animationDelay: "0.1s",
                      }}
                    ></div>
                    <div
                      className="h-4 bg-gradient-to-r from-muted to-muted/50 rounded w-2/3 animate-pulse"
                      style={{
                        animationDelay: "0.2s",
                      }}
                    ></div>
                  </div>
                </div>
              ))
            ) : newsArticles.length > 0 ? (
              newsArticles.map((article, i) => (
                <Link
                  key={article.id}
                  to={`/news/${article.slug}`}
                  className="group bg-card rounded-2xl border border-border hover:border-primary/50 overflow-hidden transition-all duration-300 hover:shadow-lg animate-fade-in block"
                  style={{
                    animationDelay: `${i * 0.1}s`,
                  }}
                >
                  <div className="h-48 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center overflow-hidden">
                    {article.featured_image_url ? (
                      <img
                        src={article.featured_image_url}
                        alt={article.title}
                        loading="lazy"
                        decoding="async"
                        width={400}
                        height={192}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <FileText className="w-16 h-16 text-primary/50" />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-3 py-1 bg-primary/10 text-primary rounded-full">
                        News
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(article.published_at), "dd MMM yyyy")}
                      </span>
                    </div>
                    <h3 className="font-bold text-xl mb-3 group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-muted-foreground mb-4 line-clamp-2">{article.excerpt}</p>
                    <span className="text-primary font-semibold text-sm hover:underline inline-flex items-center gap-1">
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-muted-foreground">No news articles available at the moment.</p>
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/news">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold"
              >
                View All News
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* University Partners Section */}
      {
        <section
          ref={partnersSection.ref}
          className={`py-20 bg-gradient-to-br from-muted/30 via-background to-muted/20 transition-all duration-1000 ${partnersSection.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 animate-fade-in">
              <span className="text-primary font-bold text-sm uppercase tracking-wider">Our Partners</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 mt-2">Trusted UK Universities</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                We partner with 50+ prestigious UK universities to help you achieve your educational goals
              </p>
            </div>

            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 3000,
                }),
              ]}
              className="w-full max-w-6xl mx-auto"
            >
              <CarouselContent className="-ml-2.5">
                {partnerUniversities.map((uni, idx) => (
                  <CarouselItem key={idx} className="pl-2.5 basis-1/2 md:basis-1/3 lg:basis-1/4">
                    <div className="bg-card rounded-full border border-border p-0 flex items-center justify-center w-32 h-32 mx-auto hover:border-primary/50 hover:scale-70 hover:shadow-lg transition-all duration-300 overflow-hidden">
                      <img
                        src={uni.img}
                        alt={`${uni.name} partner logo`}
                        loading="lazy"
                        decoding="async"
                        width={128}
                        height={128}
                        className="w-full h-full object-cover rounded-full"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </section>
      }

      {/* CTA Section */}
      <section
        ref={ctaSection.ref}
        className={`py-24 text-white relative overflow-hidden transition-all duration-1000 ${ctaSection.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        {/* Parallax Background Image with Black Tint */}
        <div
          ref={ctaParallax.ref}
          className="absolute inset-0 -inset-y-20 bg-cover bg-bottom"
          style={{
            backgroundImage: `url(${CTAbg})`,
          }}
        />
        <div className="absolute inset-0 backdrop-blur-sm bg-blue-800/50"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className={`inline-block px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full border border-primary/30 mb-6 transition-all duration-700 ${ctaSection.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}
              style={{ transitionDelay: "400ms" }}
            >
              <span className="font-bold text-slate-950">Ready to Begin Your UK Journey?</span>
            </div>
            <h2
              className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight transition-all duration-700 ${ctaSection.isVisible ? "opacity-100" : "opacity-0"}`}
              style={{ transitionDelay: "600ms", minHeight: "4.5rem" }}
            >
              {typedText || "Get in Touch with Us Today"}
            </h2>
            <p
              className={`text-xl md:text-2xl text-secondary-foreground/90 mb-10 leading-relaxed transition-all duration-700 ${ctaSection.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}
              style={{ transitionDelay: "800ms" }}
            >
              Our expert team is ready to guide you through every step of your education and immigration journey
            </p>
            <div
              className={`flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 ${ctaSection.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"}`}
              style={{ transitionDelay: "1000ms" }}
            >
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-lg px-10 py-7 shadow-elegant hover:scale-105 transition-transform"
                >
                  Contact Us Now
                </Button>
              </Link>
              <Link
                to="/reachus"
                onClick={() => trackFBEvent('ViewContent', { content_name: 'Homepage CTA – Register', content_category: 'Registration' })}
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-primary/30 hover:bg-primary/10 backdrop-blur-sm font-bold text-lg px-10 py-7 text-neutral-800 hover:scale-105 transition-transform"
                >
                  Register as Student
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section - Lazy loaded */}
      <ErrorBoundary name="home-google-reviews" silent>
        <Suspense fallback={<div className="min-h-[560px] py-20 bg-muted/30" aria-hidden="true" />}>
          <GoogleReviews />
        </Suspense>
      </ErrorBoundary>

      {/* Footer */}
      <Footer />
      <FloatingButtons />
      <HomeRegisterButton />
    </div>
  );
};
export default Index;
